import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { getCurrentEnvironment } from "../../Selectors/app";
import NestedDataViewer from "./components/NestedDataViewer";
import { useAnalyzeSchema } from "./hooks/useAnalyzeSchema";
import MonacoEditor from "@monaco-editor/react";
import TextInput from "../../Components/Inputs/Text";
import Button from "../../Components/Button";
import { format, parseISO } from "date-fns";
import css from "./styles.styl";
import { useValidateSchemaMutation, useCollections } from "../../Api/services/schemaReview";

const App = () => {
	const [schema, setSchema] = useState(`z.object({
        name: z.string(),
        age: z.number().min(18),
        email: z.string().email(),
    });`);
	const [collectionName, setCollectionName] = useState("");
	const [validationData, setValidationData] = useState({
		totalInvalidCount: 0,
		failingIds: [],
		totalCompaniesAffected: 0,
		jsonSchema: null,
	});

	const { name: env } = useSelector(getCurrentEnvironment);

	const {
		data: collections = [],
		isPending: isLoadingCollections,
		refetch: refetchCollections,
	} = useCollections(env);

	const {
		mutate: validateSchemaMutation,
		isPending: isPendingValidation,
		isError: isErrorValidation,
		isSuccess: isSuccessValidation,
		data: validationResponse,
	} = useValidateSchemaMutation();

	const {
		asOf,
		selectedCollection,
		handleAnalyzeCollection,
		progress,
		schema: currentSchema,
		hasLimit,
		setHasLimit,
		sampleSize,
		setSampleSize,
		onAnalyze,
		isFromCache,
		noCache,
		setNoCache,
	} = useAnalyzeSchema();

	useEffect(() => {
		if (env) {
			refetchCollections();
		}
	}, [env, refetchCollections]);

	useEffect(() => {
		if (isSuccessValidation) {
			setValidationData({ ...validationResponse });
		}
	}, [isSuccessValidation, validationResponse]);

	const handleValidate = () => {
		if (collectionName && schema) {
			validateSchemaMutation({ schema, collectionName, env });
		}
	};

	const downloadSchema = () => {
		if (!currentSchema) return;

		// Create a Blob with the JSON data
		const blob = new Blob([JSON.stringify(currentSchema, null, 2)], { type: "application/json" });

		// Create download link
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = `current-schema-${selectedCollection}-${new Date().toISOString()}.json`;

		// Trigger download
		document.body.appendChild(link);
		link.click();

		// Cleanup
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	};

	return (
		<div className={css.container}>
			<div className={css.leftSection}>
				<h2 className={css.sectionTitle}>Collections & Analyze</h2>
				<select
					value={collectionName}
					onChange={(e) => {
						handleAnalyzeCollection(e.target.value);
						setCollectionName(e.target.value);
					}}
					className={css.selectDropdown}
					disabled={!collections || isLoadingCollections}
				>
					<option value="" disabled>
						{isLoadingCollections ? "Loading..." : "Select a Collection"}
					</option>
					{collections.map((collection) => (
						<option key={collection} value={collection}>
							{collection}
						</option>
					))}
				</select>
				{selectedCollection && (
					<div className={css.analysisControls}>
						<label className={css.label}>
							<input type="checkbox" onChange={(e) => setHasLimit(e.target.checked)} checked={hasLimit} />
							Sample Size
						</label>
						{hasLimit && (
							<TextInput
								type="number"
								value={sampleSize}
								onChange={(val) => setSampleSize(val)}
								className={css.input}
							/>
						)}
						{isFromCache && (
							<label className={css.label}>
								<input
									type="checkbox"
									onChange={(e) => setNoCache(e.target.checked)}
									checked={noCache}
								/>
								No cached data
							</label>
						)}

						<Button onClick={onAnalyze} disabled={progress}>
							{progress ? `Processing (${progress}%)` : "Reanalyze"}
						</Button>
						<Button onClick={downloadSchema} disabled={progress}>
							Download JSON Schema Results
						</Button>
						<hr />
						{currentSchema && (
							<div>
								<h3 className={css.subTitle}>
									Schema as of {format(parseISO(asOf), "MM/dd/yyyy hh:mm a")}
								</h3>
								<NestedDataViewer data={currentSchema} />
							</div>
						)}
					</div>
				)}
			</div>

			<div className={css.rightSection}>
				<div>
					<h2 className={css.sectionTitle}>Schema Playground</h2>
					<MonacoEditor
						height="400px"
						defaultLanguage="javascript"
						value={schema}
						onChange={(newValue) => setSchema(newValue || "")}
					/>
					<Button onClick={handleValidate} disabled={isPendingValidation}>
						Validate Schema
					</Button>
				</div>
				<hr />
				<div>
					<h3 className={css.subTitle}>Validation Results</h3>
					{isPendingValidation && <div className={css.spinner}>Validating...</div>}
					{isErrorValidation && <div className={css.errorMessage}>Validation Failed</div>}
					{!isPendingValidation && validationData && !isErrorValidation && (
						<table className={css.resultsTable}>
							<tbody>
								<tr>
									<th>Total Invalid Documents</th>
									<td>{validationData.totalInvalidCount}</td>
								</tr>
								<tr>
									<th>Total Companies Affected</th>
									<td>{validationData.totalCompaniesAffected}</td>
								</tr>
								<tr>
									<th>Invalid Document IDs</th>
									<td>
										<ul className={css.typeList}>
											{validationData.failingIds.length ? (
												validationData.failingIds.map((id) => (
													<li key={id} className={css.typeItem}>
														{id}
													</li>
												))
											) : (
												<li className={css.validMessage}>All documents are valid!</li>
											)}
										</ul>
									</td>
								</tr>
							</tbody>
						</table>
					)}
				</div>
			</div>
		</div>
	);
};

export default App;
